/**
 * Weight-Denominated Primitives
 * 
 * These types enforce weight-first architecture at compile time.
 * Value (dollars) is always derived, never primitive.
 */

/**
 * Opaque type for grams - prevents accidental mixing with numbers
 */
export type Grams = number & { readonly __brand: 'Grams' };

/**
 * Opaque type for troy ounces
 */
export type TroyOunces = number & { readonly __brand: 'TroyOunces' };

/**
 * Create a Grams value (factory function enforces intent)
 */
export function grams(value: number): Grams {
  if (value < 0) throw new Error('Weight cannot be negative');
  if (!Number.isFinite(value)) throw new Error('Weight must be finite');
  return value as Grams;
}

/**
 * Create a TroyOunces value
 */
export function troyOunces(value: number): TroyOunces {
  if (value < 0) throw new Error('Weight cannot be negative');
  if (!Number.isFinite(value)) throw new Error('Weight must be finite');
  return value as TroyOunces;
}

/**
 * Weight unit conversions (deterministic, lossless where possible)
 */
export const WeightConversion = {
  GRAMS_PER_TROY_OUNCE: 31.1034768,
  
  toGrams(oz: TroyOunces): Grams {
    return grams(oz * WeightConversion.GRAMS_PER_TROY_OUNCE);
  },
  
  toTroyOunces(g: Grams): TroyOunces {
    return troyOunces(g / WeightConversion.GRAMS_PER_TROY_OUNCE);
  }
} as const;

/**
 * Unwrap weight values for display only
 * Name makes it clear this is for presentation, not calculation
 */
export function displayGrams(g: Grams): number {
  return g as number;
}

export function displayTroyOunces(oz: TroyOunces): number {
  return oz as number;
}
